import { useState, useEffect } from 'react';
import useSWR from 'swr';
import { ExternalLink, Check, Wallet, AlertCircle, RefreshCw } from 'lucide-react';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectComponentsProvider, ConnectPayouts } from '@stripe/react-connect-js';
import api from '../utils/api';
import { toast } from 'sonner';
import TabPageLayout, { PageLoader, DataCard, EmptyState } from './TabPageLayout';
import { Button } from './ui/button';
import StripeConnectBanner from './StripeConnectBanner';

const PayoutsTabNew = () => {
  const [stripeConnectInstance, setStripeConnectInstance] = useState(null);
  const [initError, setInitError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);

  // Use SWR for status fetching
  const { data: status, error: statusError, isLoading, mutate } = useSWR(
    '/organizations/stripe-connect/status',
    async (url) => {
      const res = await api.get(url);
      return res.data;
    },
    { revalidateOnFocus: true }
  );

  const initializeStripe = async () => {
    if (isInitializing) return;
    setIsInitializing(true);
    try {
      setInitError(null);
      const instance = await loadConnectAndInitialize({
        publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
        fetchClientSecret: async () => {
          const res = await api.post('/organizations/stripe-connect/account-session');
          return res.data.clientSecret;
        },
        appearance: {
          variables: {
            colorPrimary: '#2563EB',
            fontFamily: 'Inter, system-ui, sans-serif',
          },
        },
      });
      setStripeConnectInstance(instance);
    } catch (err) {
      console.error('Error initializing Stripe:', err);
      setInitError('Failed to load payout settings');
      toast.error('Failed to load payout settings');
    } finally {
      setIsInitializing(false);
    }
  };

  // Initialize Stripe when onboarding is complete
  useEffect(() => {
    if (status?.onboardingComplete && !stripeConnectInstance && !isInitializing) {
      initializeStripe();
    }
  }, [status?.onboardingComplete, stripeConnectInstance, isInitializing]);

  // Loading state
  if (isLoading) {
    return (
      <TabPageLayout title="Payouts">
        <PageLoader />
      </TabPageLayout>
    );
  }

  // Error state
  if (statusError) {
    return (
      <TabPageLayout title="Payouts">
        <EmptyState
          icon={<AlertCircle />}
          title="Failed to load"
          description="Could not load your payout settings. Please try again."
          action={
            <Button onClick={() => mutate()} className="bg-blue-600 hover:bg-blue-700 text-white h-10">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          }
        />
      </TabPageLayout>
    );
  }

  // Not onboarded - show setup banner
  if (!status?.onboardingComplete) {
    return (
      <TabPageLayout 
        title="Payouts"
        subtitle="Set up payment processing to receive payouts"
      >
        <StripeConnectBanner />
      </TabPageLayout>
    );
  }

  // Stripe init error
  if (initError) {
    return (
      <TabPageLayout title="Payouts" subtitle="Manage your earnings and payouts">
        <EmptyState
          icon={<AlertCircle />}
          title="Connection Error"
          description={initError}
          action={
            <Button onClick={initializeStripe} className="bg-blue-600 hover:bg-blue-700 text-white h-10">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry Connection
            </Button>
          }
        />
      </TabPageLayout>
    );
  }

  // Waiting for Stripe instance to initialize
  if (!stripeConnectInstance) {
    return (
      <TabPageLayout title="Payouts" subtitle="Manage your earnings and payouts">
        <PageLoader />
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Payouts"
      subtitle="Manage your earnings and payouts"
      actions={
        <a
          href={status?.accountId ? `https://dashboard.stripe.com/${status.accountId}` : "https://dashboard.stripe.com"}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors h-10 whitespace-nowrap"
        >
          <ExternalLink className="w-4 h-4 flex-shrink-0" />
          <span className="hidden sm:inline">Stripe Dashboard</span>
          <span className="sm:hidden">Stripe</span>
        </a>
      }
    >
      <div className="space-y-6">
        {/* Status Indicator */}
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg w-fit">
          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
          <span className="text-sm font-medium text-green-700">Payment Processing Active</span>
        </div>

        {/* Stripe Payouts Component - Contains balance AND transaction history */}
        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
          <DataCard>
            <div className="min-h-[400px] overflow-x-auto -mx-4 sm:mx-0">
              <div className="min-w-[320px] px-4 sm:px-0">
                <ConnectPayouts />
              </div>
            </div>
          </DataCard>
        </ConnectComponentsProvider>
      </div>
    </TabPageLayout>
  );
};

export default PayoutsTabNew;
